import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { filter, flatMap, includes, isEmpty, map, some, uniq } from 'es-toolkit/compat';
import { getInnerRowIdFromUnwoundRowMetadata } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import pluralize from 'pluralize';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'redux/hooks';

import { createMockCasesForSlides } from '../ProceduresPage/usePendingSlides';
import { ManifestWorkspaceSurface } from './CaseManifestControls';
import { CaseManifestSourcePanel } from './CaseManifestSourcePanel';
import { buildSingleCaseManifestDraft, ManifestDialogUrlState } from './dialogState';
import { revalidateSlideCaseAssignmentOptions } from './loadSlideCaseAssignmentOptions';
import { addManifestDraftCases } from './manifestDraft';
import { SlideSelectionTableRow } from './SlideSelectionTable';
import { useSlideCaseAssignmentOptions } from './useSlideCaseAssignmentOptions';

interface ManifestSourceSelectionOptions {
  open: boolean;
  labId: string;
  studyId: string;
  activeWorkspaceSurface: ManifestWorkspaceSurface;
  currentDraftCases: Procedure[];
  pendingSlidesCasesToDisplay: Procedure[];
  sourceScope: 'study' | 'lab';
  canReadActiveSource: boolean;
  canReadStudySource: boolean;
  canIncludeAssigned: boolean;
  isPendingSlidesLoading: boolean;
  activePendingSlidesError: unknown;
  sourceAssignmentStrategy: ManifestDialogUrlState['sourceAssignmentStrategy'];
  pendingSlidesSelectionAction: ManifestDialogUrlState['pendingSlidesSelectionAction'];
  disabled: boolean;
  updateManifestDialogUrlState: (
    updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
  ) => void;
}

export const useManifestSourceSelection = ({
  open,
  labId,
  studyId,
  activeWorkspaceSurface,
  currentDraftCases,
  pendingSlidesCasesToDisplay,
  sourceScope,
  canReadActiveSource,
  canReadStudySource,
  canIncludeAssigned,
  isPendingSlidesLoading,
  activePendingSlidesError,
  sourceAssignmentStrategy,
  pendingSlidesSelectionAction,
  disabled,
  updateManifestDialogUrlState,
}: ManifestSourceSelectionOptions) => {
  const { profile, generationId } = useAppSelector((state) => state.auth);
  const { selectedRows } = useRowSelectionContext();
  const [selectedSourceSlideIds, setSelectedSourceSlideIds] = useState<string[]>(() =>
    getInnerRowIdFromUnwoundRowMetadata(selectedRows as string[])
  );
  const [assignedSource, setAssignedSource] = useState<{ key: string; includeAssigned: boolean } | null>(null);
  const [isRevalidatingSource, setIsRevalidatingSource] = useState(false);
  const [sourceSelectionMessage, setSourceSelectionMessage] = useState<string | null>(null);
  const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState<HTMLElement | null>(null);
  const sourceValidationController = useRef<AbortController | null>(null);
  const isSlideSource =
    activeWorkspaceSurface === 'Study Lost and Found Slides' || activeWorkspaceSurface === 'Lab Lost and Found Slides';
  const sourceIdentity = JSON.stringify([profile?.userId, generationId, labId, studyId, activeWorkspaceSurface]);
  const includeAssigned = assignedSource?.key === sourceIdentity && assignedSource.includeAssigned;
  const assignedOptions = useSlideCaseAssignmentOptions({
    request: { labId, targetStudyId: studyId, scope: sourceScope, includeAssigned: true },
    callerId: profile?.userId || '',
    generationId: generationId || '',
    enabled: open && isSlideSource && includeAssigned && canIncludeAssigned && canReadActiveSource,
  });
  const draftSlideIdentity = JSON.stringify(map(flatMap(currentDraftCases, 'slides'), 'id'));
  useEffect(() => {
    sourceValidationController.current?.abort();
    setIsRevalidatingSource(false);
    setSourceSelectionMessage(null);
    return () => sourceValidationController.current?.abort();
  }, [open, sourceIdentity, includeAssigned, canReadActiveSource, canIncludeAssigned, draftSlideIdentity]);
  const isSourceLoading =
    isRevalidatingSource || (includeAssigned ? assignedOptions.isLoading : isPendingSlidesLoading);
  const sourceUnavailable =
    !canReadActiveSource || (includeAssigned ? !assignedOptions.isComplete : Boolean(activePendingSlidesError));
  const pendingSlideSelectionRows = useMemo<SlideSelectionTableRow[]>(
    () =>
      includeAssigned
        ? map(
            filter(
              assignedOptions.items || [],
              ({ slide }) => !some(currentDraftCases, (draftCase) => some(draftCase.slides, { id: slide.id }))
            ),
            ({ slide, isAssigned, assignments, hasRestrictedAssignments }) => ({
              slide,
              sourceContextItems: slide.sourceStudyId ? [`Source study: ${slide.sourceStudyId}`] : [],
              assignment: isAssigned
                ? [
                    ...map(
                      assignments,
                      ({ caseId, caseLabel, studyId: owningStudyId }) =>
                        `${caseLabel} (case ${caseId}, study ${owningStudyId})`
                    ),
                    ...(hasRestrictedAssignments ? ['Assigned to a restricted case'] : []),
                  ].join('; ')
                : 'Needs case assignment',
            })
          )
        : flatMap(pendingSlidesCasesToDisplay, (pendingCase) =>
            map(pendingCase.slides, (slide) => ({
              slide: { ...slide, labId: slide.labId || pendingCase.labId },
              sourceContextItems: filter(
                [
                  slide.sourceStudyId ? `Source study: ${slide.sourceStudyId}` : undefined,
                  pendingCase.labId && pendingCase.labId !== labId ? `Source lab: ${pendingCase.labId}` : undefined,
                ],
                (context): context is string => Boolean(context)
              ),
              assignment: 'Needs case assignment',
            }))
          ),
    [labId, pendingSlidesCasesToDisplay, includeAssigned, assignedOptions.items, currentDraftCases]
  );

  const addCasesToManifest = useCallback(
    (casesToAdd: Procedure[], assignToExistingCases = false) => {
      if (isEmpty(casesToAdd)) return;
      updateManifestDialogUrlState((previousState) =>
        addManifestDraftCases(previousState, casesToAdd, assignToExistingCases)
      );
    },
    [updateManifestDialogUrlState]
  );
  const addSelectedSlidesToManifest = useCallback(
    async ({ groupIntoSingleCase, scope }: { groupIntoSingleCase: boolean; scope: 'study' | 'lab' }) => {
      if (!canIncludeAssigned || !selectedSourceSlideIds.length) return;
      sourceValidationController.current?.abort();
      const controller = new AbortController();
      sourceValidationController.current = controller;
      setIsRevalidatingSource(true);
      setSourceSelectionMessage(null);
      try {
        const items = await revalidateSlideCaseAssignmentOptions(
          { labId, targetStudyId: studyId, scope },
          selectedSourceSlideIds,
          controller.signal
        );
        if (controller.signal.aborted) return;
        const validItems = filter(
          items,
          ({ slide }) =>
            includes(selectedSourceSlideIds, slide.id) &&
            !some(currentDraftCases, (draftCase) => some(draftCase.slides, { id: slide.id }))
        );
        const validIds = map(validItems, ({ slide }) => slide.id);
        const missingCount = selectedSourceSlideIds.length - validIds.length;
        if (missingCount > 0) {
          setSelectedSourceSlideIds(validIds);
          setSourceSelectionMessage(
            `${missingCount} selected slides are no longer available in this source or already in the case plan. Review the remaining selection before adding.`
          );
          return;
        }
        const slides = map(validItems, 'slide');
        addCasesToManifest(
          groupIntoSingleCase
            ? buildSingleCaseManifestDraft({ slides, labId })
            : createMockCasesForSlides(slides, labId),
          sourceAssignmentStrategy === 'addToExistingCase' && !groupIntoSingleCase
        );
        setSelectedSourceSlideIds([]);
      } catch (error) {
        if (!controller.signal.aborted) {
          setSourceSelectionMessage(
            'Could not verify the selected slides. Your selection and case plan are unchanged. Try adding again.'
          );
        }
      } finally {
        if (sourceValidationController.current === controller && !controller.signal.aborted)
          setIsRevalidatingSource(false);
      }
    },
    [
      addCasesToManifest,
      canIncludeAssigned,
      currentDraftCases,
      labId,
      selectedSourceSlideIds,
      sourceAssignmentStrategy,
      studyId,
    ]
  );
  const addSelectedPendingSlidesToManifest = useCallback(
    async (groupIntoSingleCase = false) => {
      if (isSourceLoading || sourceUnavailable) return;
      await addSelectedSlidesToManifest({ groupIntoSingleCase, scope: sourceScope });
    },
    [addSelectedSlidesToManifest, isSourceLoading, sourceScope, sourceUnavailable]
  );
  // Dataset-selected slides belong to this study, so the action intent reads study scope on any workspace surface.
  useEffect(() => {
    if (!pendingSlidesSelectionAction || isPendingSlidesLoading || isRevalidatingSource || !canReadStudySource) return;
    updateManifestDialogUrlState({ pendingSlidesSelectionAction: undefined });
    void addSelectedSlidesToManifest({
      groupIntoSingleCase: pendingSlidesSelectionAction === 'groupSelectedSlidesIntoSingleCase',
      scope: 'study',
    });
  }, [
    pendingSlidesSelectionAction,
    isPendingSlidesLoading,
    isRevalidatingSource,
    canReadStudySource,
    updateManifestDialogUrlState,
    addSelectedSlidesToManifest,
  ]);
  const addDirectSlidesToManifest = useCallback(
    (slides: Slide[]) =>
      updateManifestDialogUrlState((previousState) => ({
        ...previousState,
        manualManifestSlideIdsToLoad: uniq([
          ...(previousState.manualManifestSlideIdsToLoad || []),
          ...map(slides, 'id'),
        ]),
        manualManifestErrorMessages: [],
      })),
    [updateManifestDialogUrlState]
  );

  const selectedPendingSlidesCount = selectedSourceSlideIds.length;
  const sourceName =
    activeWorkspaceSurface === 'Study Lost and Found Slides'
      ? 'study lost and found slides'
      : 'lab lost and found slides';
  const sourcePanelProps: ComponentProps<typeof CaseManifestSourcePanel> = {
    addButtonText:
      selectedPendingSlidesCount === 0
        ? 'Select Slides To Add (0 selected)'
        : `Add ${pluralize('Selected Slide', selectedPendingSlidesCount, true)}`,
    assignmentMenuAnchor,
    assignmentStrategy: sourceAssignmentStrategy,
    canIncludeAssigned,
    canReadSource: canReadActiveSource,
    description:
      activeWorkspaceSurface === 'Study Lost and Found Slides'
        ? 'Select slides already scoped to this study to include in this organization.'
        : 'Select slides to include in this organization.',
    disabled,
    hasAssignedError: Boolean(assignedOptions.error),
    hasPendingError: Boolean(activePendingSlidesError),
    includeAssigned: Boolean(includeAssigned),
    isLoading: isSourceLoading,
    loadingText: isRevalidatingSource
      ? 'Verifying selected slides...'
      : includeAssigned
      ? `Loading slides and assignments... ${assignedOptions.loadedCount} slides loaded`
      : `Loading ${sourceName}...`,
    onAdd: () => void addSelectedPendingSlidesToManifest(sourceAssignmentStrategy === 'groupIntoOneCase'),
    onAssignmentMenuAnchorChange: setAssignmentMenuAnchor,
    onAssignmentStrategyChange: (strategy) => updateManifestDialogUrlState({ sourceAssignmentStrategy: strategy }),
    onIncludeAssignedChange: (checked) => setAssignedSource({ key: sourceIdentity, includeAssigned: checked }),
    onRetry: () => void assignedOptions.refetch(),
    onSelectedSlideIdsChange: setSelectedSourceSlideIds,
    rows: pendingSlideSelectionRows,
    selectedSlideCount: selectedPendingSlidesCount,
    selectedSlideIds: selectedSourceSlideIds,
    selectionMessage: sourceSelectionMessage,
    selectionScopeKey: `${sourceIdentity}:${includeAssigned}`,
    sourceName,
    sourceUnavailable,
  };
  return { addDirectSlidesToManifest, sourcePanelProps };
};
